import type Phaser from 'phaser';
import type { ToolId } from '../../content/tools';
import type { UpgradeKind } from '../../meta/economy';
import type { BoostId } from '../../sim/boosts';

/**
 * Item icons (tools and upgrades): 16 px top-down/side pixel drawings in the game's style, one
 * frame each in the `items` sheet (artSheets.ts). Rows are 16 characters; '.' is transparent.
 */
export const ITEMS_KEY = 'items';

export type ItemIcon = ToolId | UpgradeKind | BoostId;

/** Frame order in the sheet (never reorder: art sheets follow this layout). */
export const ITEM_FRAMES: readonly ItemIcon[] = [
  'bikeHorn',
  'rake',
  'stringTrimmer',
  'hedgeTrimmer',
  'shovel',
  'pruningSaw',
  'tank',
  'hp',
  'blades',
  'engine',
  'steering',
  'reverse',
  'shield',
  'turbo',
  'magnet',
  'treat',
  'sprinklerKey',
  'tires',
  'bumper',
  'reserve',
];

const PALETTE: Readonly<Record<string, string>> = {
  k: '#1f2023',
  g: '#8f969d',
  G: '#c9ced3',
  m: '#5d6369',
  L: '#4a8a3a',
  w: '#b07a45',
  W: '#d9a066',
  n: '#7a4f2a',
  r: '#d8432f',
  R: '#f07a5f',
  q: '#a83222',
  o: '#e8862c',
  O: '#f6ad62',
  u: '#b4621a',
  y: '#f2c94c',
  Y: '#fff2a8',
  b: '#b8860b',
  p: '#c9a26b',
  P: '#e2c48f',
  t: '#8f6a3e',
  v: '#e9e3cf',
  V: '#ffffff',
  e: '#3a7bd5',
  E: '#74a6ea',
};

const DESIGNS: Record<ItemIcon, readonly string[]> = {
  // A black rubber bumper bar on chrome brackets, yellow-and-black at the corners.
  bumper: [
    '................',
    '................',
    '................',
    '...g........g...',
    '...G........G...',
    '...g........g...',
    '.kkkkkkkkkkkkkk.',
    'kykmmmmmmmmmmyk.',
    'kkykkkkkkkkkkykk',
    'kykkkkkkkkkkkkyk',
    'kkmmmmmmmmmmmmkk',
    '.kkkkkkkkkkkkkk.',
    '................',
    '................',
    '................',
    '................',
  ],
  // A green jerry can held in reserve (the red one is the tank).
  reserve: [
    '................',
    '.....kkkk.......',
    '....kGGGGk.kk...',
    '....kkkkkk.kgk..',
    '...kLLLLLLLLLk..',
    '..kLLLLLLLLLLLk.',
    '..kLkkkkkkkkLLk.',
    '..kLkLLLLLLkLLk.',
    '..kLkLLYYLLkLLk.',
    '..kLkLYYYYLkLLk.',
    '..kLkLLYYLLkLLk.',
    '..kLkkkkkkkkLLk.',
    '..kLLLLLLLLLLLk.',
    '..kkkkkkkkkkkkk.',
    '................',
    '................',
  ],
  // Hardened Tires: a chunky tire, deep treads, a shine on the rubber.
  tires: [
    '................',
    '.....kkkkkk.....',
    '...kkmmkkmmkk...',
    '..kmkkmmmmkkmk..',
    '.kmkkkkkkkkkkmk.',
    '.kmkkkggggkkkmk.',
    'kmmkkgGGGGgkkmmk',
    'kkmkgGkkkkGgkmkk',
    'kkmkgGkkkkGgkmkk',
    'kmmkkgGGGGgkkmmk',
    '.kmkkkggggkkkmk.',
    '.kmkkkkkkkkkkmk.',
    '..kmkkmmmmkkmk..',
    '...kkmmkkmmkk...',
    '.....kkkkkk.....',
    '................',
  ],
  bikeHorn: [
    '................',
    '................',
    '................',
    '................',
    '............yb..',
    '..........yyYb..',
    '...qq...yyyYyb..',
    '..qrRqggyyyyyb..',
    '.qrRRrggyyyyyb..',
    '.qrrrrggbyyyyb..',
    '..qqqq..bbbyyb..',
    '...........bbb..',
    '................',
    '................',
    '................',
    '................',
  ],
  rake: [
    '.............nW.',
    '............nW..',
    '...........nW...',
    '..........nW....',
    '.........nW.....',
    '........nW......',
    '.......nW.......',
    '......nW........',
    '.....nW.........',
    '..GmgnWg........',
    '.Gm..gmG........',
    'Gm..g..mG.......',
    'm..g..m..G......',
    '..g..m...m......',
    '.g..m...........',
    '................',
  ],
  stringTrimmer: [
    '................',
    '..kk............',
    '.kook...........',
    '.kOok...........',
    '..kk.k..........',
    '......k.........',
    '.......k........',
    '........k.......',
    '.........k......',
    '..........k.....',
    '...........kk...',
    '..........kGGk..',
    '..........kGGk..',
    '...........kk...',
    '..........VVVV..',
    '................',
  ],
  hedgeTrimmer: [
    '................',
    '................',
    '................',
    '................',
    '....kk..........',
    '...kOOk.........',
    '..OOooOGGGGGGGG.',
    '..oooooGmGmGmGmG',
    '..uoooommmmmmmm.',
    '...uuuk.........',
    '....kk..........',
    '................',
    '................',
    '................',
    '................',
    '................',
  ],
  shovel: [
    '..kkk...........',
    '..k.k...........',
    '..kWk...........',
    '...nW...........',
    '....nW..........',
    '.....nW.........',
    '......nW........',
    '.......nW.......',
    '........ggG.....',
    '.......gGGGG....',
    '........gGGGG...',
    '.........gGGGm..',
    '..........gGGm..',
    '...........gm...',
    '................',
    '................',
  ],
  pruningSaw: [
    '................',
    '................',
    '..........kk....',
    '.........kGGk...',
    '........kGGGGk..',
    '.......kGGGGgk..',
    '......kGGGGgk...',
    '.....kGGGGgk....',
    '....kgGGGgk.....',
    '...kwkgGgk......',
    '..kwWwkgk.......',
    '.kwWwk.k........',
    '.kWwk...........',
    '..kk............',
    '................',
    '................',
  ],
  tank: [
    '................',
    '................',
    '......kkk.......',
    '.....k..k.......',
    '....rRRRRk......',
    '...rRRRRRRrr....',
    '...rRrrrrrrqk...',
    '...rrrrYrrrqk...',
    '...rrrYYYrrq....',
    '...rrrrYrrrq....',
    '...rrrrrrrrq....',
    '...rrrrrrrrq....',
    '...qqqqqqqqq....',
    '................',
    '................',
    '................',
  ],
  hp: [
    '................',
    '................',
    '...GGGGGGGGGG...',
    '...GggggggggG...',
    '...GgVggggVgm...',
    '...Gggggggggm...',
    '...Gggggggggm...',
    '...Gggggggggm...',
    '....GgVgggVgm...',
    '....Ggggggggm...',
    '.....Gggggm.....',
    '......Gggm......',
    '.......Gm.......',
    '................',
    '................',
    '................',
  ],
  // Sharpened Blades: a rotary blade with a glint.
  blades: [
    '................',
    '............Y...',
    '...........YVY..',
    '............Y...',
    '................',
    '.k............k.',
    'kGk..........kGk',
    'kGGkkkkkkkkkkGGk',
    'kVVVVVVVVVVVVVVk',
    'kGGGGGGkkGGGGGGk',
    'kggggggkkggggggk',
    '.kkkkkkkkkkkkkk.',
    '................',
    '................',
    '................',
    '................',
  ],
  engine: [
    '................',
    '................',
    '.....kkkkkk.....',
    '...kkGGGGGGkk...',
    '..kGGgGGGGgRRk..',
    '..kGGGGGGGGRrk..',
    '.kGgGGGGGGrGRRk.',
    '.kGGGGGGGrGGGGk.',
    '.kGGGGGkrkGGGGk.',
    '.kgGGGGkkkGGGgk.',
    '.kGGGGGGGGGGGGk.',
    '..kkkkkkkkkkkk..',
    '................',
    '....kmmmmmmk....',
    '................',
    '................',
  ],
  steering: [
    '................',
    '.....kkkkkk.....',
    '...kkmmmmmmkk...',
    '..kmmkkkkkkmmk..',
    '.kmmk......kmmk.',
    '.kmk........kmk.',
    'kmmkkkkkkkkkkmmk',
    'kmmGGGGkkGGGGmmk',
    'kmmkkkkkkkkkkmmk',
    '.kmk...km...kmk.',
    '.kmmk..km..kmmk.',
    '..kmmkkkmkkmmk..',
    '...kkmmmmmmkk...',
    '.....kkkkkk.....',
    '................',
    '................',
  ],
  reverse: [
    '................',
    '................',
    '....kkkkkkk.....',
    '...kyyyyyyyk....',
    '..kyyYkkkkyyk...',
    '..kyYk....kyyk..',
    '..kyyk....kyyk..',
    '..kyyk....kyyk..',
    '..kyyk..kkkyykkk',
    '..kyyk..kyyyyyyk',
    '..kyyk...kyyyyk.',
    '..kyyk....kyyk..',
    '..kyyk.....kk...',
    '..kkkk..........',
    '................',
    '................',
  ],
  shield: [
    '................',
    '.....kkkkkk.....',
    '...kkEEEEEEkk...',
    '..kEEVVEEEEEEk..',
    '.kEEVEEEEEEEEEk.',
    '.kEVEEEEEEEEEEk.',
    'kEEEEEEEEEEEEEEk',
    'kEEEEEEEEEEEEEek',
    'kEEEEEEEEEEEEEek',
    'kEEEEEEEEEEEEeek',
    '.kEEEEEEEEEEEek.',
    '.kEEEEEEEEEEeek.',
    '..kEEEEEEEeeek..',
    '...kkeeeeeekk...',
    '.....kkkkkk.....',
    '................',
  ],
  turbo: [
    '................',
    '.........kkkk...',
    '........kYYyk...',
    '.......kYYyk....',
    '......kYYyk.....',
    '.....kYYyk......',
    '....kYYYYkkkk...',
    '...kYYYYYYYYk...',
    '...kkkkyYYYk....',
    '......kyYYk.....',
    '.....kyYYk......',
    '....kyYYk.......',
    '...kyYk.........',
    '...kyk..........',
    '...kk...........',
    '................',
  ],
  magnet: [
    '................',
    '................',
    '....kkkkkkkk....',
    '...kRrrrrrrrk...',
    '..kRrkkkkkkrrk..',
    '..kRk......krk..',
    '..kRk......krk..',
    '..kRk......krk..',
    '..kRk......krk..',
    '..kkk......kkk..',
    '..kGk......kGk..',
    '..kGk......kGk..',
    '..kkk......kkk..',
    '................',
    '..y..........y..',
    '................',
  ],
  treat: [
    '................',
    '..........L.L...',
    '.........LLL....',
    '........kLLk....',
    '.......kOOk.....',
    '......kOooOk....',
    '.....kOoooOk....',
    '....kOoooOk.....',
    '....kOooOk......',
    '...kOooOk.......',
    '...kOoOk........',
    '..kOoOk.........',
    '..kOOk..........',
    '..kkk...........',
    '................',
    '................',
  ],
  sprinklerKey: [
    '................',
    '................',
    '.....kkkk.......',
    '....kGGGGk......',
    '...kGk..kGk.....',
    '...kGk..kGk.....',
    '....kGGGGk......',
    '.....kGGk.......',
    '......kGk.......',
    '......kGk.......',
    '......kGGk......',
    '......kGk.......',
    '......kGGk......',
    '......kkk.......',
    '................',
    '................',
  ],
};

const S = 16;

export function createItems(scene: Phaser.Scene): void {
  const tex = scene.textures.createCanvas(ITEMS_KEY, S * ITEM_FRAMES.length, S);
  if (!tex) throw new Error('Could not create item texture');
  ITEM_FRAMES.forEach((id, i) => {
    DESIGNS[id].forEach((row, y) => {
      for (let x = 0; x < row.length; x++) {
        const color = PALETTE[row[x]!];
        if (!color) continue;
        tex.context.fillStyle = color;
        tex.context.fillRect(i * S + x, y, 1, 1);
      }
    });
  });
  tex.refresh();
  ITEM_FRAMES.forEach((_, i) => tex.add(i, 0, i * S, 0, S, S));
}

/** Frame index of an item's icon. */
export const itemFrame = (id: ItemIcon): number => ITEM_FRAMES.indexOf(id);

/** Design rows, for tests. */
export const ITEM_DESIGNS: Readonly<Record<ItemIcon, readonly string[]>> = DESIGNS;
export const ITEM_PALETTE = PALETTE;
